<script>
export default {
  name: 'EVoucherCard',
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  computed: {
    voucher() {
      return this.item.voucher || {};
    },
    hasImage() {
      return this.voucher.image_url || this.voucher.presigned_image_url;
    },
    imageUrl() {
      return this.voucher.presigned_image_url || this.voucher.image_url;
    }
  }
};
</script>

<template>
  <div class="card rounded-xl border shadow-sm hover:shadow-md transition-all bg-white dark:bg-slate-800 overflow-hidden group flex flex-col h-full">
    
    <!-- Consistent Image Container -->
    <div class="relative bg-slate-100 dark:bg-slate-700 overflow-hidden" 
         :class="hasImage ? 'h-40' : 'h-32'">
      
      <!-- Image or Placeholder -->
      <img
        v-if="hasImage"
        :src="imageUrl"
        :alt="voucher.category"
        class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
      />
      
      <!-- Placeholder when no image -->
      <div v-else class="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-600 dark:to-slate-700">
        <div class="text-center p-4">
          <svg class="w-8 h-8 text-slate-400 dark:text-slate-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
          </svg>
          <p class="text-xs text-slate-500 dark:text-slate-400 font-medium">No image</p>
        </div>
      </div>

      <!-- Status Badge Overlay - Consistent Position -->
      <div class="absolute top-3 right-3">
        <span
          @click.stop="$emit('toggle-active')"
          class="px-2 py-1 rounded-full text-xs font-medium shadow-md backdrop-blur-sm border border-white/20 cursor-pointer transition-opacity hover:opacity-80"
          :class="voucher.is_active 
            ? 'bg-green-100 text-green-700 dark:bg-green-700 dark:text-green-100' 
            : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400'"
        >
          {{ voucher.is_active ? 'Active' : 'Inactive' }}
        </span>
      </div>
      
      <!-- Value Badge Overlay - Consistent Position -->
      <div class="absolute top-3 left-3">
        <span class="px-2 py-1 rounded-md text-sm font-semibold bg-black/70 text-white backdrop-blur-sm">
          {{ voucher.value }}
        </span>
      </div>
      
      <!-- Image Overlay Gradient (only when image exists) -->
      <div v-if="hasImage" class="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
    </div>

    <!-- Content Section - Consistent Height -->
    <div class="p-4 flex-1 flex flex-col">
      
      <!-- Category and Description - Consistent Layout -->
      <div class="mb-3 flex-1">
        <h2 class="font-bold text-lg text-slate-900 dark:text-slate-100 line-clamp-1 mb-2">
          {{ voucher.category }}
        </h2>
        <p class="text-sm text-slate-500 dark:text-slate-400 line-clamp-2 min-h-[2.5rem]">
          {{ voucher.template_message || 'No description provided' }}
        </p>
      </div>

      <!-- Services Section - Fixed at Bottom -->
      <div class="space-y-3 mt-auto">
        <!-- Services Info -->
        <div class="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-600">
          <div class="flex items-center space-x-2">
            <span class="text-xs text-slate-500 dark:text-slate-400 font-medium">
              {{ item.service_count || 0 }} service{{ item.service_count !== 1 ? 's' : '' }}
            </span>
            <div 
              v-if="item.service_count > 0"
              class="w-1.5 h-1.5 rounded-full bg-green-500"
            ></div>
            <span 
              v-else
              class="text-xs text-amber-600 dark:text-amber-400 font-medium"
            >
              Not linked
            </span>
          </div>
          
          <button 
            class="text-woot-500 hover:text-woot-700 transition-colors duration-200 p-1 rounded hover:bg-woot-50 dark:hover:bg-woot-900/20"
            @click="$emit('services')" 
            title="Link services"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"/>
            </svg>
          </button>
        </div>

        <!-- Action Buttons - Consistent Spacing -->
        <div class="flex gap-2 justify-end">
          <woot-button 
            size="small"
            variant="smooth"
            icon="edit" 
            color-scheme="secondary" 
            @click="$emit('edit')"
            class="flex-1 max-w-[48%] justify-center opacity-80 hover:opacity-100 transition-opacity"
          />
          <woot-button 
            size="small"
            variant="smooth"
            icon="delete" 
            color-scheme="alert" 
            @click="$emit('delete')"
            class="flex-1 max-w-[48%] justify-center opacity-80 hover:opacity-100 transition-opacity"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.line-clamp-1 {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
}

.line-clamp-2 {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.group:hover .group-hover\:scale-105 {
  transform: scale(1.05);
}

/* Ensure consistent card heights in grid */
.card {
  min-height: 320px;
}
</style>